import { useCallback, useMemo } from 'react';
import { useTracking } from 'react-tracking';

import { Text, Grid } from '@simplywallst/ui-core';

import SetFairValue from '../../../../FairValue/SetFairValue';
import { TableRowCell } from '../../Table';
import { MoreOptions } from '../../MoreOptions';
import { ValuationMetricTooltip, GrowthMetricTooltip } from '../../Tooltips';

import type { Watchlist, WatchlistItem } from '@/hooks/useWatchlist';
import PositiveNegativeValue from '@/components/PositiveNegativeValue';
import MiniPriceChart, { DeferRender } from '@/components/MiniPriceChart';
import { CompanyIntrinsicDiscount } from '@/components/CompanyIntrinsicDiscount';
import Tooltip from '@/components/Tooltip';

import {
  formatterCurrency,
  formatterPercentage,
  formatterFactor,
} from '@/utilities/format';

import { useCompanyPeriodReturn } from '../../../useCompanyPeriodReturn';

import {
  Chart,
  CellRouterLink,
  WatchlistTableRow,
  TooltipLabel,
} from './styled';

type Props = {
  index: number;
  watchlistId: Watchlist['id'];
  watchlistItem: WatchlistItem;
  onWatchlistItemSelect: (id: WatchlistItem['id'] | null) => void;
  'data-cy-id'?: string;
};

const DeprecatedWatchlistRowDesktopExpanded = ({
  index,
  watchlistId,
  watchlistItem,
  onWatchlistItemSelect,
  'data-cy-id': dataCyId,
}: Props) => {
  const { id: watchlistItemId, company, fairValue } = watchlistItem;
  const {
    tickerSymbol,
    id: companyId,
    name: companyName,
    canonicalURL,
  } = company;
  const { trading_item_currency_iso: companyCurrencyISO } =
    company.data.currencyInfo;
  const {
    return7d: companyReturn7d,
    lastSharePrice: companySharePrice,
    preferredRelativeMultiple,
    pe,
    pb,
    priceToSales: ps,
    priceTarget,
  } = company.analysisValue;
  const {
    revenueGrowthAnnual: salesGrowth,
    netIncomeGrowthAnnual: earningsGrowth,
  } = company.analysisFuture;
  const { analystCount } = company.analysisMisc;

  const { period, return: companyReturn } = useCompanyPeriodReturn(company);

  const valuation = useMemo(() => {
    if (preferredRelativeMultiple === 'pe') return pe;
    if (preferredRelativeMultiple === 'pb') return pb;
    if (preferredRelativeMultiple === 'ps') return ps;
    return null;
  }, [preferredRelativeMultiple, pe, pb, ps]);

  const { Track, trackEvent } = useTracking({
    modifier: 'desktopRow',
  });

  const handleRouterLinkClick = useCallback(() => {
    trackEvent({
      index,
      action: 'click',
      canonicalURL,
    });
  }, [trackEvent, canonicalURL, index]);

  const handleClick = useCallback(() => {
    onWatchlistItemSelect(watchlistItemId);
    trackEvent({
      action: 'click',
    });
  }, [onWatchlistItemSelect, watchlistItemId, trackEvent]);

  const earningsGrowthFormatted = formatterPercentage(earningsGrowth);
  const salesGrowthFormatted = formatterPercentage(salesGrowth);

  return (
    <Track>
      <WatchlistTableRow data-cy-id={dataCyId} tabIndex={0}>
        <TableRowCell>
          <Grid
            data-cy-id="company-column"
            alignItems="center"
            onClick={handleClick}
          >
            <CellRouterLink
              data-cy-id="company-column"
              to={canonicalURL}
              onClick={handleRouterLinkClick}
            >
              <Text
                data-cy-id="ticker-symbol"
                typography="xSmallStrong"
                color="brand01"
                textTransform="uppercase"
              >
                <strong>{tickerSymbol}</strong>
              </Text>
            </CellRouterLink>
            <Text
              typography="xSmall"
              color="whiteRGBA"
              opacity={0.5}
              lineClamp={1}
            >
              {companyName}
            </Text>
          </Grid>
        </TableRowCell>
        <TableRowCell data-cy-id="share-price">
          <Grid alignItems="center" onClick={handleClick}>
            <Text typography="xSmall" isTruncated>
              {formatterCurrency(companySharePrice, companyCurrencyISO)}
            </Text>
            <Text typography="tinyCondensed" color="whiteRGBA" opacity={0.5}>
              <CompanyIntrinsicDiscount
                fairValue={fairValue}
                company={company}
              />
            </Text>
          </Grid>
        </TableRowCell>
        <TableRowCell>
          <SetFairValue watchlistItem={watchlistItem} />
        </TableRowCell>
        <TableRowCell data-cy-id="valuation-cell">
          <Grid alignItems="center" onClick={handleClick}>
            <Text as="span" typography="xSmall">
              <Tooltip
                key={`table-tooltip-${companyId}`}
                title={
                  <ValuationMetricTooltip
                    pb={formatterFactor(pb)}
                    pe={formatterFactor(pe)}
                    ps={formatterFactor(ps)}
                  />
                }
                light
                zIndex={99999}
              >
                <TooltipLabel aria-label="Valuation metrics">
                  {preferredRelativeMultiple?.toUpperCase()}
                </TooltipLabel>
              </Tooltip>
              {`  ${formatterFactor(valuation)}`}
            </Text>
          </Grid>
        </TableRowCell>
        <TableRowCell data-cy-id="growth-cell">
          <Grid alignItems="center" onClick={handleClick}>
            <Text as="span" typography="xSmall">
              <Tooltip
                key={`table-tooltip-${companyId}`}
                title={
                  <GrowthMetricTooltip
                    analystCount={analystCount}
                    earningsGrowth={earningsGrowthFormatted}
                    salesGrowth={salesGrowthFormatted}
                  />
                }
                light
                zIndex={99999}
              >
                <TooltipLabel aria-label="Growth metrics">
                  {earningsGrowth ? 'E' : 'S'}
                </TooltipLabel>
              </Tooltip>
              {`  ${
                earningsGrowth ? earningsGrowthFormatted : salesGrowthFormatted
              }`}
            </Text>
          </Grid>
        </TableRowCell>
        <TableRowCell data-cy-id="analyst-target-cell">
          <Grid alignItems="center" onClick={handleClick}>
            <Text as="span" typography="xSmall">
              {formatterCurrency(priceTarget, companyCurrencyISO)}
            </Text>
          </Grid>
        </TableRowCell>
        <TableRowCell>
          <Grid alignItems="center" justifyContent="end" onClick={handleClick}>
            <PositiveNegativeValue value={companyReturn7d}>
              {formatterPercentage(companyReturn7d)}
            </PositiveNegativeValue>
          </Grid>
        </TableRowCell>
        <TableRowCell data-cy-id="period-return">
          <Grid alignItems="center" justifyContent="end" onClick={handleClick}>
            <PositiveNegativeValue value={companyReturn}>
              {formatterPercentage(companyReturn)}
            </PositiveNegativeValue>
          </Grid>
        </TableRowCell>
        <TableRowCell colSpan={2}>
          <Chart>
            <DeferRender>
              <MiniPriceChart
                companyId={companyId}
                period={period}
                currencyISO={companyCurrencyISO}
              />
            </DeferRender>
          </Chart>
        </TableRowCell>
        <TableRowCell>
          <Grid alignItems="center">
            <MoreOptions
              watchlistId={watchlistId}
              watchlistItem={watchlistItem}
            />
          </Grid>
        </TableRowCell>
      </WatchlistTableRow>
    </Track>
  );
};

export default DeprecatedWatchlistRowDesktopExpanded;
